// =========================================================================
// TRON address encoding — deliberately standalone.
//
// Extracted from hd.mjs so the co-sign page can validate and render addresses
// without pulling in BIP-39 mnemonics, BIP-32 derivation and the secp256k1
// curve. That page is the one surface where "small enough to audit once and
// pin" is a security property rather than a preference, so every dependency it
// does not need is worth removing.
//
// Depends on nothing but sha256 and base58.
// =========================================================================

import { sha256 } from '@noble/hashes/sha256';
import { base58check as base58checkFactory } from '@scure/base';

// TRON uses the same double-SHA256 checksum as Bitcoin.
const base58check = base58checkFactory(sha256);

/** Mainnet address version byte — "T…" in base58. */
export const TRON_ADDRESS_PREFIX = 0x41;

/** base58 length is fixed; anything else is junk and must not reach a decoder. */
export const TRON_BASE58_LENGTH = 34;

/**
 * base58check → 21-byte body (0x41 || hash160). Throws on a bad checksum, so a
 * single mistyped character cannot survive into a transaction.
 *
 * The length is checked first, here rather than by each caller: base58
 * decoding is superlinear, and a 200,001-character "address" took 86 seconds
 * when the check was a separate function that three callers had not called.
 * Every TRON mainnet address is 34 characters.
 */
export function decodeTronAddress(address) {
  if (typeof address !== 'string' || address.length !== TRON_BASE58_LENGTH) {
    throw new Error('not a valid TRON mainnet address');
  }
  const body = base58check.decode(address);
  if (body.length !== 21 || body[0] !== TRON_ADDRESS_PREFIX) {
    throw new Error('not a valid TRON mainnet address');
  }
  return body;
}

/** 21-byte body → base58check. */
export function encodeTronAddress(body21) {
  if (body21.length !== 21 || body21[0] !== TRON_ADDRESS_PREFIX) {
    throw new Error('expected a 21-byte body starting 0x41');
  }
  return base58check.encode(body21);
}

/**
 * Cheap shape check to run BEFORE base58 decoding, which is superlinear — a
 * hostile 100k-character "address" must be rejected without being decoded.
 */
export function assertPlausibleAddress(a, what = 'address') {
  if (typeof a !== 'string' || a.length !== TRON_BASE58_LENGTH || a[0] !== 'T') {
    throw new Error(`${what} is not a well-formed TRON address`);
  }
}
